'use client'
import type { ReactNode } from 'react'
import { useState } from 'react'
import createCache from '@emotion/cache'
import { CacheProvider } from '@emotion/react'
import { useServerInsertedHTML } from 'next/navigation'

interface InsertedStyle {
  name: string
  isGlobal: boolean
}

/**
 * Emotion SSR registry for the App Router (Chakra v2's own `CacheProvider` from
 * `@chakra-ui/next-js` is a no-op since 2.4).
 *
 * Without it emotion writes every style as an inline `<style>` next to its element in
 * `<body>`. On the client emotion moves those tags into `<head>` and `<Global>` re-inserts
 * the global ones while React is hydrating in time slices — React's hydration cursor then
 * points at a moved `<style>` instead of the first `<div>` and the whole page falls back to
 * a client render (minified React error #418, production builds only).
 *
 * `compat` stops the inline tags; the styles are flushed through `useServerInsertedHTML`,
 * which Next streams into `<head>`.
 */
export const EmotionRegistry = ({ children }: { children: ReactNode }) => {
  const [{ cache, flush }] = useState(() => {
    const cache = createCache({ key: 'css' })
    cache.compat = true

    const prevInsert = cache.insert
    let inserted: InsertedStyle[] = []

    cache.insert = (...args) => {
      const [selector, serialized] = args

      if (cache.inserted[serialized.name] === undefined) {
        inserted.push({ name: serialized.name, isGlobal: !selector })
      }

      return prevInsert(...args)
    }

    const flush = () => {
      const prev = inserted
      inserted = []
      return prev
    }

    return { cache, flush }
  })

  useServerInsertedHTML(() => {
    const names = flush()

    if (names.length === 0) {
      return null
    }

    let styles = ''
    let dataEmotion = cache.key
    const globals: { name: string; style: string }[] = []

    for (const { name, isGlobal } of names) {
      const style = cache.inserted[name]

      if (typeof style !== 'string') {
        continue
      }

      if (isGlobal) {
        globals.push({ name, style })
      } else {
        styles += style
        dataEmotion += ` ${name}`
      }
    }

    return (
      <>
        {globals.map(({ name, style }) => (
          <style
            key={name}
            dangerouslySetInnerHTML={{ __html: style }}
            data-emotion={`${cache.key}-global ${name}`}
          />
        ))}
        {styles ? (
          <style dangerouslySetInnerHTML={{ __html: styles }} data-emotion={dataEmotion} />
        ) : null}
      </>
    )
  })

  return <CacheProvider value={cache}>{children}</CacheProvider>
}
